import { useEffect, useRef, useState } from 'react'
import { api, ErroreApi } from '../servizi/api.ts'
import { useSessione } from '../servizi/sessione.tsx'
import { AreaTesto, Bottone, Campo, Etichetta, Modale, Scelta, type TonoEtichetta } from './base.tsx'
import { Avviso } from './Pezzi.tsx'
import { AvvisoModulo, erroriDellApi, messaggioErrore } from './errori-modulo.tsx'
import { useFuocoSulPrimoErrore } from './persona-campi.tsx'
import { SceltaStanza } from './stanze.tsx'
import { opzioniSedi } from '../servizi/sedi.ts'

/**
 * Ricontatti e appuntamenti di una persona (465; richiesta di CIDIS del 19/10/2026: «le ho fatto
 * appuntamento con l'avvocato: dove vado per inserire queste info?»). Il modulo per fissarne o
 * modificarne uno, la finestra per chiuderlo con l'esito e i campi che il modulo dell'intervento
 * riusa per «Fissa anche un ricontatto».
 *
 * Dalla 560 «Fissa un appuntamento»: un appuntamento in sede, con ora, durata, sede, stanza e, se si
 * ripete, fino a 26 date. Compare nell'agenda della sede; la stanza occupata blocca, la sede chiusa avvisa.
 */

export type Ricontatto = {
  id: string; persona_id: string; percorso_id: string | null; percorso?: string | null
  previsto_il: string; ora: string | null; motivo: string | null; esito: string | null
  stato: 'programmato' | 'fatto' | 'non_presentata' | 'annullato' | 'scaduto'
  fatto_il: string | null; responsabile_id: string | null; responsabile: string | null
  /** 560: ricontatto o appuntamento in sede, con durata, sede e stanza. */
  tipo: 'ricontatto' | 'appuntamento'; durata_minuti: number | null; sede_id: string | null; sede_nome: string | null
  risorsa_id: string | null; risorsa_nome: string | null; serie_id: string | null
}

/** GET /persone/:id/ricontatti: i ricontatti della scheda e quello che serve a fissarne uno. */
export type RicontattiScheda = {
  /** Falso per chi apre la scheda ma non ne gestisce i ricontatti: la sezione non compare. */
  puo_gestire: boolean
  ricontatti: Ricontatto[]
  colleghi: { id: string; nome: string; cognome: string }[]
  percorsi: { id: string; nome: string; data_apertura: string }[]
}

export const STATI: Record<Ricontatto['stato'], { etichetta: string; tono: TonoEtichetta }> = {
  programmato: { etichetta: 'in programma', tono: 'accento' },
  fatto: { etichetta: 'fatto', tono: 'positivo' },
  non_presentata: { etichetta: 'non si è presentata', tono: 'attenzione' },
  annullato: { etichetta: 'annullato', tono: 'neutro' },
  scaduto: { etichetta: 'senza esito', tono: 'attenzione' },
}

/** Giorno di oggi a Roma, AAAA-MM-GG: il minimo del campo giorno. */
export const oggiRoma = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Rome' }).format(new Date())

/** «21/10/2026 alle 10:30», oppure senza ora. */
export const quandoRicontatto = (r: { previsto_il: string; ora: string | null }) =>
  `${r.previsto_il.slice(0, 10).split('-').reverse().join('/')}${r.ora ? ` alle ${r.ora}` : ''}`

/** Un ricontatto in programma con il giorno gia' passato. */
export const inRitardo = (r: Ricontatto) => r.stato === 'programmato' && r.previsto_il.slice(0, 10) < oggiRoma()

export type ValoriRicontatto = { previsto_il: string; ora: string; motivo: string; responsabile_id: string; percorso_id: string }

export const ricontattoVuoto = (io: string | undefined): ValoriRicontatto =>
  ({ previsto_il: '', ora: '', motivo: '', responsabile_id: io ?? '', percorso_id: '' })

/** Corpo per l'API: i campi vuoti diventano nulli. */
export const corpoRicontatto = (v: ValoriRicontatto, conPercorso = true) => ({
  previsto_il: v.previsto_il, ora: v.ora || null, motivo: v.motivo.trim(),
  responsabile_id: v.responsabile_id || null, ...(conPercorso ? { percorso_id: v.percorso_id || null } : {}),
})

/** Errori trovati prima dell'invio: giorno e motivo. */
export function controllaRicontatto(v: ValoriRicontatto, prefisso = ''): Record<string, string> {
  const errori: Record<string, string> = {}
  if (!v.previsto_il) errori[`${prefisso}previsto_il`] = 'Indica il giorno.'
  if (v.motivo.trim().length < 3) errori[`${prefisso}motivo`] = 'Scrivi il motivo, per esempio «appuntamento con l’avvocato».'
  return errori
}

export const ETICHETTE_RICONTATTO = {
  previsto_il: 'Giorno', ora: 'Ora', motivo: 'Motivo', responsabile_id: 'Chi lo segue', percorso_id: 'Percorso',
}

/**
 * I campi di un ricontatto. `prefisso` distingue gli errori quando i campi stanno dentro un altro
 * modulo (`ricontatto.` nel modulo dell'intervento); `percorsi` assente vuol dire che il percorso e'
 * deciso altrove (quello dell'intervento).
 */
export function CampiRicontatto({ valori, cambia, colleghi, percorsi, errori, prefisso = '', minimo, oraObbligatoria = false }: {
  valori: ValoriRicontatto
  cambia: (v: ValoriRicontatto) => void
  colleghi: RicontattiScheda['colleghi']
  percorsi?: RicontattiScheda['percorsi']
  errori: Record<string, string>
  prefisso?: string
  /** Giorno minimo ammesso: oggi, oppure il giorno gia' fissato su un ricontatto rimasto indietro. */
  minimo?: string
  /** L'appuntamento in sede (560) ha sempre l'ora. */
  oraObbligatoria?: boolean
}) {
  const { io } = useSessione()
  const imposta = <K extends keyof ValoriRicontatto>(k: K, x: string) => cambia({ ...valori, [k]: x })
  const err = (k: keyof ValoriRicontatto) => errori[`${prefisso}${k}`] ?? null
  // Chi fissa il ricontatto compare sempre fra chi puo' seguirlo, anche se l'elenco non lo porta.
  const elenco = io && !colleghi.some((c) => c.id === io.id)
    ? [{ id: io.id, nome: io.nome, cognome: io.cognome }, ...colleghi] : colleghi
  return (
    <>
      <div className="griglia-modulo">
        <Campo etichetta="Giorno" tipo="date" obbligatorio valore={valori.previsto_il} min={minimo ?? oggiRoma()}
               cambia={(x) => imposta('previsto_il', x)} errore={err('previsto_il')} />
        <Campo etichetta="Ora" tipo="time" valore={valori.ora} cambia={(x) => imposta('ora', x)} obbligatorio={oraObbligatoria}
               errore={err('ora')} aiuto={oraObbligatoria ? undefined : 'Facoltativa.'} />
      </div>
      <Campo etichetta="Motivo" obbligatorio maxLength={500} valore={valori.motivo}
             cambia={(x) => imposta('motivo', x)} errore={err('motivo')}
             segnaposto="Per esempio: appuntamento con l’avvocato"
             aiuto="Si conserva cifrato, come le note della scheda." />
      <div className="griglia-modulo">
        <Scelta etichetta="Chi lo segue" valore={valori.responsabile_id} cambia={(x) => imposta('responsabile_id', x)}
                opzioni={elenco.map((c) => ({
                  valore: c.id, etichetta: `${`${c.cognome} ${c.nome}`.trim()}${c.id === io?.id ? ' (tu)' : ''}`,
                }))}
                errore={err('responsabile_id')}
                aiuto="Lo ritrova nel cruscotto e nella pagina Ricontatti, e riceve un promemoria due giorni prima." />
        {percorsi && (
          <Scelta etichetta="Percorso" valore={valori.percorso_id} cambia={(x) => imposta('percorso_id', x)}
                  vuota="— nessuno in particolare —"
                  opzioni={percorsi.map((p) => ({ valore: p.id, etichetta: p.nome }))}
                  errore={err('percorso_id')} />
        )}
      </div>
    </>
  )
}

const DURATE = [15, 30, 45, 60, 90, 120, 180].map((m) => ({ valore: String(m), etichetta: m < 60 ? `${m} minuti` : `${m / 60} or${m === 60 ? 'a' : 'e'}` }))
const FREQUENZE = [
  { valore: 'settimanale', etichetta: 'Ogni settimana' },
  { valore: 'quindicinale', etichetta: 'Ogni due settimane' },
  { valore: 'mensile', etichetta: 'Ogni mese, nello stesso giorno' },
]

/** Fissa un ricontatto o un appuntamento nuovo, oppure modifica quello indicato. */
export function ModuloRicontatto({ personaId, ricontatto, dati, chiudi, fatto, appuntamento: nuovoAppuntamento = false, sedePersona = null }: {
  personaId: string
  ricontatto: Ricontatto | null
  dati: RicontattiScheda
  chiudi: () => void
  fatto: (testo: string) => void
  /** Nuovo appuntamento in sede (560); per una riga esistente decide il suo tipo. */
  appuntamento?: boolean
  /** La sede della scheda: quella proposta per l'appuntamento. */
  sedePersona?: string | null
}) {
  const { io, sedi } = useSessione()
  const appuntamento = ricontatto ? ricontatto.tipo === 'appuntamento' : nuovoAppuntamento
  const [durata, setDurata] = useState(String(ricontatto?.durata_minuti ?? 60))
  const [sede, setSede] = useState(ricontatto ? ricontatto.sede_id ?? '' : sedePersona ?? '')
  const [stanza, setStanza] = useState(ricontatto?.risorsa_id ?? '')
  const [frequenza, setFrequenza] = useState('')
  const [volte, setVolte] = useState('4')
  const [stanzaOccupata, setStanzaOccupata] = useState<string | null>(null)
  const [valori, setValori] = useState<ValoriRicontatto>(() => (ricontatto
    ? {
      previsto_il: ricontatto.previsto_il, ora: ricontatto.ora ?? '', motivo: ricontatto.motivo ?? '',
      responsabile_id: ricontatto.responsabile_id ?? '', percorso_id: ricontatto.percorso_id ?? '',
    }
    : ricontattoVuoto(io?.id)))
  const [errori, setErrori] = useState<Record<string, string>>({})
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const [tentativo, setTentativo] = useState(0)
  // La sede della scheda e' chiusa a quell'ora (556): si fissa lo stesso solo confermando.
  const [fuoriOrario, setFuoriOrario] = useState<string | null>(null)
  const radice = useRef<HTMLDivElement>(null)
  useFuocoSulPrimoErrore(radice, tentativo)

  // Chi lo segue oggi resta fra le voci anche se non potrebbe piu' essere scelto.
  const colleghi = ricontatto?.responsabile_id && !dati.colleghi.some((c) => c.id === ricontatto.responsabile_id)
    ? [...dati.colleghi, { id: ricontatto.responsabile_id, nome: ricontatto.responsabile ?? 'Collega', cognome: '' }]
    : dati.colleghi
  // Il percorso gia' scelto resta fra le voci anche se nel frattempo e' stato chiuso.
  const percorsi = ricontatto?.percorso_id && !dati.percorsi.some((p) => p.id === ricontatto.percorso_id)
    ? [...dati.percorsi, { id: ricontatto.percorso_id, nome: `${ricontatto.percorso ?? 'Percorso'} (chiuso)`, data_apertura: '' }]
    : dati.percorsi

  async function salva(conferma = false) {
    const trovati = controllaRicontatto(valori)
    if (appuntamento && !valori.ora) trovati.ora = 'Indica l’ora dell’appuntamento.'
    setErrori(trovati); setErrore(null); setFuoriOrario(null); setStanzaOccupata(null)
    if (Object.keys(trovati).length) {
      setErrore('Mancano alcuni dati.'); setTentativo((n) => n + 1); return
    }
    setInCorso(true)
    const campiAppuntamento = appuntamento
      ? { durata_minuti: Number(durata), sede_id: sede || null, risorsa_id: stanza || null } : {}
    const cosa = appuntamento ? 'Appuntamento' : 'Ricontatto'
    try {
      if (ricontatto) {
        await api.patch(`/follow-up/${ricontatto.id}`, { ...corpoRicontatto(valori), ...campiAppuntamento, conferma_fuori_orario: conferma })
        fatto(`${cosa} aggiornato.`)
      } else {
        const r = await api.post<{ ids?: string[] }>('/follow-up', {
          persona_id: personaId, ...corpoRicontatto(valori), ...campiAppuntamento, conferma_fuori_orario: conferma,
          ...(appuntamento ? { tipo: 'appuntamento', ...(frequenza ? { ripetizione: { frequenza, volte: Number(volte) } } : {}) } : {}),
        })
        const chi = valori.responsabile_id && valori.responsabile_id !== io?.id
          ? dati.colleghi.find((c) => c.id === valori.responsabile_id) : null
        const quante = (r.ids?.length ?? 1) > 1 ? ` (${r.ids!.length} date, la prima)` : ''
        fatto(`${cosa} fissato per il ${quandoRicontatto({ previsto_il: valori.previsto_il, ora: valori.ora || null })}${quante}`
          + (chi ? `: lo segue ${chi.nome} ${chi.cognome}, che riceve un avviso.` : '.'))
      }
    } catch (e) {
      if (e instanceof ErroreApi && e.codice === 'FUORI_ORARIO') {
        setFuoriOrario(e.message)
        return
      }
      if (e instanceof ErroreApi && e.codice === 'STANZA_OCCUPATA') {
        setStanzaOccupata(e.message)
        return
      }
      setErrori(erroriDellApi(e))
      setErrore(messaggioErrore(e, 'La scheda non rientra più fra quelle di cui puoi gestire i ricontatti.'))
      setTentativo((n) => n + 1)
    } finally {
      setInCorso(false)
    }
  }

  const minimo = ricontatto && ricontatto.previsto_il < oggiRoma() ? ricontatto.previsto_il : undefined
  return (
    <Modale larga titolo={appuntamento ? (ricontatto ? 'Modifica l’appuntamento' : 'Fissa un appuntamento')
      : (ricontatto ? 'Modifica il ricontatto' : 'Fissa un ricontatto')} chiudi={chiudi}
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Lascia stare</Bottone>
              <Bottone variante="primario" inCorso={inCorso} onClick={() => void salva()}>
                {ricontatto ? 'Salva' : appuntamento ? 'Fissa l’appuntamento' : 'Fissa il ricontatto'}
              </Bottone>
            </>}>
      <div ref={radice}>
        {!ricontatto && (
          <p className="piccolo tenue">
            {appuntamento
              ? 'Un colloquio in sede con la persona: ora, durata, sede e stanza. Compare nell’agenda della sede; '
                + 'quando è passato si chiude con com’è andato, e se è fatto si registra l’intervento.'
              : 'Un appuntamento preso per la persona (per esempio con l’avvocato) o un momento in cui '
                + 'risentirla. Quando è passato, si chiude con com’è andata.'}
          </p>
        )}
        <CampiRicontatto valori={valori} cambia={setValori} colleghi={colleghi} percorsi={percorsi}
                         errori={errori} minimo={minimo} oraObbligatoria={appuntamento} />
        {appuntamento && (
          <>
            <div className="griglia-modulo">
              <Scelta etichetta="Durata" valore={durata} cambia={setDurata}
                      opzioni={DURATE.some((d) => d.valore === durata) ? DURATE : [{ valore: durata, etichetta: `${durata} minuti` }, ...DURATE]} />
              <Scelta etichetta="Sede" valore={sede} cambia={(v) => { setSede(v); setStanza('') }}
                      vuota="— la sede della scheda —" opzioni={opzioniSedi(sedi)} />
              <SceltaStanza sedeId={sede || sedePersona} valore={stanza} cambia={setStanza} colloqui />
            </div>
            {!ricontatto && (
              <div className="griglia-modulo">
                <Scelta etichetta="Si ripete" valore={frequenza} cambia={setFrequenza} vuota="No" opzioni={FREQUENZE}
                        aiuto="Un appuntamento per ogni data, alla stessa ora: ognuno si sposta e si chiude da solo." />
                {frequenza && (
                  <Campo tipo="number" etichetta="Quanti in tutto" valore={volte} cambia={setVolte} min={2} max={26}
                         aiuto="Al più 26, il primo compreso." />
                )}
              </div>
            )}
          </>
        )}
        <AvvisoModulo errore={errore} errori={errori} etichette={ETICHETTE_RICONTATTO} />
        {stanzaOccupata && <Avviso tipo="errore">{stanzaOccupata}</Avviso>}
        {fuoriOrario && <AvvisoFuoriOrario testo={fuoriOrario} inCorso={inCorso}
                                           conferma={() => void salva(true)} rinuncia={() => setFuoriOrario(null)} />}
      </div>
    </Modale>
  )
}

/**
 * Il ricontatto cade con la sede chiusa (fuori orario, chiusura, festivita'; 556): il messaggio
 * dell'API e la scelta, come per le mediazioni. Lo usa anche il modulo dell'intervento.
 */
export function AvvisoFuoriOrario({ testo, inCorso, conferma, rinuncia }: {
  testo: string; inCorso: boolean; conferma: () => void; rinuncia: () => void
}) {
  return (
    <Avviso tipo="attenzione">
      {testo}
      <div className="riga">
        <Bottone variante="secondario" dimensione="piccolo" inCorso={inCorso} onClick={conferma}>
          Fissa lo stesso
        </Bottone>
        <Bottone variante="discreto" dimensione="piccolo" onClick={rinuncia}>Scelgo un altro orario</Bottone>
      </div>
    </Avviso>
  )
}

/** Chiude un ricontatto con l'esito. `fatto` riceve anche lo stato: da un appuntamento fatto si registra l'intervento. */
export function ChiudiRicontatto({ ricontatto, chiudi, fatto }: {
  ricontatto: Ricontatto; chiudi: () => void; fatto: (testo: string, stato?: string) => void
}) {
  const [stato, setStato] = useState('')
  const [nota, setNota] = useState('')
  const [errore, setErrore] = useState<string | null>(null)
  const [erroreStato, setErroreStato] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const futuro = ricontatto.previsto_il > oggiRoma()

  async function registra() {
    setErrore(null); setErroreStato(null)
    if (!stato) { setErroreStato('Scegli com’è andata.'); return }
    setInCorso(true)
    try {
      await api.post(`/follow-up/${ricontatto.id}/esito`, { stato, esito: nota.trim() || null })
      fatto(`${ricontatto.tipo === 'appuntamento' ? 'Appuntamento' : 'Ricontatto'} del ${quandoRicontatto(ricontatto)}: `
        + `${STATI[stato as Ricontatto['stato']].etichetta}.`, stato)
    } catch (e) {
      setErrore(messaggioErrore(e, 'La scheda non rientra più fra quelle di cui puoi gestire i ricontatti.'))
    } finally {
      setInCorso(false)
    }
  }

  return (
    <Modale titolo={`Com’è andato il ricontatto del ${quandoRicontatto(ricontatto)}?`} chiudi={chiudi}
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Lascia stare</Bottone>
              <Bottone variante="primario" inCorso={inCorso} onClick={() => void registra()}>Registra</Bottone>
            </>}>
      {ricontatto.motivo && <p><strong>{ricontatto.motivo}</strong></p>}
      <Scelta etichetta="Esito" obbligatorio valore={stato} cambia={setStato} vuota="— scegli —" errore={erroreStato}
              opzioni={[
                { valore: 'fatto', etichetta: 'Fatto' },
                { valore: 'non_presentata', etichetta: 'La persona non si è presentata' },
                { valore: 'annullato', etichetta: 'Annullato' },
              ]}
              aiuto={futuro && stato && stato !== 'annullato'
                ? 'Il giorno fissato non è ancora arrivato: controlla di aver scelto il ricontatto giusto.' : undefined} />
      <AreaTesto etichetta="Nota" righe={3} maxLength={1000} valore={nota} cambia={setNota}
                 aiuto="Facoltativa: com’è andata, che cosa resta da fare. Si conserva cifrata." />
      {errore && <AvvisoModulo errore={errore} />}
    </Modale>
  )
}

/**
 * Carica i dati della scheda per i ricontatti; `null` finche' arrivano, `false` se non si possono
 * gestire. Senza il permesso in nessun incarico non chiede niente.
 */
export function useRicontattiScheda(personaId: string, versione: number) {
  const { puo } = useSessione()
  const permesso = puo('follow_up.gestisci')
  const [dati, setDati] = useState<RicontattiScheda | null | false>(null)
  const [errore, setErrore] = useState<string | null>(null)
  useEffect(() => {
    let vivo = true
    if (!permesso) { setDati(false); return }
    api.get<RicontattiScheda>(`/persone/${personaId}/ricontatti`)
      .then((d) => { if (vivo) { setDati(d.puo_gestire ? d : false); setErrore(null) } })
      .catch((e) => {
        if (!vivo) return
        // Fuori dal perimetro dei ricontatti la sezione semplicemente non c'e'.
        if (e instanceof ErroreApi && e.stato === 404) setDati(false)
        else setErrore(messaggioErrore(e, ''))
      })
    return () => { vivo = false }
  }, [personaId, versione, permesso])
  return { dati, errore }
}

export function EtichettaStato({ r }: { r: Ricontatto }) {
  if (inRitardo(r)) return <Etichetta tono="attenzione">da chiudere</Etichetta>
  const s = STATI[r.stato]
  return <Etichetta tono={s.tono}>{s.etichetta}</Etichetta>
}
